import type { Site, Project, Repository } from '../types';
import { sites as staticSites, projects as staticProjects, customRepos as staticCustomRepos } from '../data';

async function saveToServer(key: string, data: any) {
  if (import.meta.env.DEV) {
    try {
      await fetch('/api/saveData', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ key, data })
      });
    } catch (e) {
      console.error('API save failed', e);
    }
  }
}

// Cloud Database configuration (JSONBin)
const JSONBIN_URL = import.meta.env.VITE_JSONBIN_BIN_ID ? `https://api.jsonbin.io/v3/b/${import.meta.env.VITE_JSONBIN_BIN_ID}` : null;
const JSONBIN_KEY = import.meta.env.VITE_JSONBIN_MASTER_KEY || '';

export async function fetchCloudData() {
  if (!JSONBIN_URL) return null;
  try {
    const res = await fetch(JSONBIN_URL, {
      headers: {
        'X-Master-Key': JSONBIN_KEY
      }
    });
    if (!res.ok) throw new Error('Cloud fetch failed');
    const data = await res.json();
    return data.record; // standard jsonbin format
  } catch (err) {
    console.warn("Could not load cloud data:", err);
    return null;
  }
}

function syncCloud() {
  if (!JSONBIN_URL) return;
  const payload = {
    sites: getSites(),
    projects: getProjects(),
    customRepos: getCustomRepos()
  };
  fetch(JSONBIN_URL, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'X-Master-Key': JSONBIN_KEY
    },
    body: JSON.stringify(payload)
  }).catch(err => console.warn("Cloud sync failed:", err));
}

const STORAGE_KEYS = {
  SITES: 'admin_sites',
  PROJECTS: 'admin_projects',
  CUSTOM_REPOS: 'admin_custom_repos',
  LOCKOUT_UNTIL: 'admin_lockout_until',
} as const;

export function getSites(): Site[] {
  try {
    const saved = localStorage.getItem(STORAGE_KEYS.SITES);
    if (saved) {
      const parsed = JSON.parse(saved);
      if (Array.isArray(parsed) && parsed.length > 0) {
        return parsed;
      }
    }
  } catch (error) {
    console.error('Failed to load sites from localStorage:', error);
  }
  return [...staticSites];
}

export function getProjects(): Project[] {
  try {
    const saved = localStorage.getItem(STORAGE_KEYS.PROJECTS);
    if (saved) {
      const parsed = JSON.parse(saved);
      if (Array.isArray(parsed) && parsed.length > 0) {
        return parsed;
      }
    }
  } catch (error) {
    console.error('Failed to load projects from localStorage:', error);
  }
  return [...staticProjects];
}

export function saveSites(sites: Site[], skipCloud = false): boolean {
  try {
    localStorage.setItem(STORAGE_KEYS.SITES, JSON.stringify(sites));
    saveToServer(STORAGE_KEYS.SITES, sites);
    if (!skipCloud) syncCloud();
    return true;
  } catch (error) {
    console.error('Failed to save sites to localStorage:', error);
    return false;
  }
}

export function saveProjects(projects: Project[], skipCloud = false): boolean {
  try {
    localStorage.setItem(STORAGE_KEYS.PROJECTS, JSON.stringify(projects));
    saveToServer(STORAGE_KEYS.PROJECTS, projects);
    if (!skipCloud) syncCloud();
    return true;
  } catch (error) {
    console.error('Failed to save projects to localStorage:', error);
    return false;
  }
}

// Custom Repos (manually added repos for repos section)
export function getCustomRepos(): Repository[] {
  try {
    const saved = localStorage.getItem(STORAGE_KEYS.CUSTOM_REPOS);
    if (saved) {
      const parsed = JSON.parse(saved);
      if (Array.isArray(parsed)) {
        return parsed;
      }
    }
  } catch (error) {
    console.error('Failed to load custom repos from localStorage:', error);
  }
  return [...staticCustomRepos];
}

export function saveCustomRepos(repos: Repository[], skipCloud = false): boolean {
  try {
    localStorage.setItem(STORAGE_KEYS.CUSTOM_REPOS, JSON.stringify(repos));
    saveToServer(STORAGE_KEYS.CUSTOM_REPOS, repos);
    if (!skipCloud) syncCloud();
    return true;
  } catch (error) {
    console.error('Failed to save custom repos to localStorage:', error);
    return false;
  }
}

// Lockout management for admin login
export function getLockoutUntil(): number {
  try {
    const saved = localStorage.getItem(STORAGE_KEYS.LOCKOUT_UNTIL);
    return saved ? parseInt(saved, 10) : 0;
  } catch {
    return 0;
  }
}

export function setLockoutUntil(timestamp: number): void {
  try {
    localStorage.setItem(STORAGE_KEYS.LOCKOUT_UNTIL, timestamp.toString());
  } catch (error) {
    console.error('Failed to save lockout:', error);
  }
}

export function clearLockout(): void {
  try {
    localStorage.removeItem(STORAGE_KEYS.LOCKOUT_UNTIL);
  } catch (error) {
    console.error('Failed to clear lockout:', error);
  }
}

export function isLockedOut(): boolean {
  const lockoutUntil = getLockoutUntil();
  return lockoutUntil > Date.now();
}

export function getRemainingLockoutTime(): number {
  const lockoutUntil = getLockoutUntil();
  return Math.max(0, lockoutUntil - Date.now());
}
